import React, { useState } from "react";
import { FeatherRefreshCw } from "@subframe/core";
import { DialogLayout } from "../../../../ui";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import {
  applyTcsSync,
  previewTcsSync,
  TcsSyncPreview,
} from "../../../../services/api";

const FIELD_LABELS: Record<string, string> = {
  tax_name: "Name",
  tax_rate: "Rate",
  nature_of_collection: "Nature of collection",
  tax_specific_type: "Section",
  is_expired: "Expired",
};

interface TcsSyncButtonProps {
  onApplied: () => void;
}

/**
 * Sync TCS rates from Zoho, preview first.
 *
 * Preview then confirm, the same contract as the TDS sync: the apply re-reads
 * from Zoho server-side rather than trusting whatever the preview returned, and
 * never deletes a local row — a rate that has been used on an invoice still has
 * to resolve, whatever Zoho lists today.
 *
 * A count summary rather than TDS's full table. There is nothing to curate on a
 * TCS rate, so the question here is only "how many, and what moves", not "which
 * of these do I need to go and fill in".
 */
export const TcsSyncButton: React.FC<TcsSyncButtonProps> = ({ onApplied }) => {
  const [preview, setPreview] = useState<TcsSyncPreview | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [applied, setApplied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handlePreview = async () => {
    setBusy(true);
    setError(null);
    setApplied(null);
    try {
      const result = await previewTcsSync();
      setPreview(result);
      setOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not read from Zoho");
    } finally {
      setBusy(false);
    }
  };

  const handleApply = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await applyTcsSync();
      setApplied(
        `${result.data.created} added, ${result.data.updated} updated.`,
      );
      onApplied();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sync failed");
    } finally {
      setBusy(false);
    }
  };

  const data = preview?.data;
  const nothingToDo =
    data !== undefined && data.new.length === 0 && data.changed.length === 0;

  return (
    <>
      <Button
        variant="brand-primary"
        size="small"
        icon={<FeatherRefreshCw />}
        onClick={handlePreview}
        disabled={busy}
      >
        {busy && !open ? "Fetching..." : "Sync TCS"}
      </Button>

      {error && !open && (
        <span className="text-caption font-caption text-red-600">{error}</span>
      )}

      <DialogLayout
        open={open}
        onOpenChange={(next: boolean) => {
          if (!next) {
            setOpen(false);
            setApplied(null);
          }
        }}
      >
        <div className="flex w-160 max-w-full flex-col items-start gap-4 bg-default-background px-8 py-6">
          <span className="text-heading-2 font-heading-2 text-default-font">
            TCS rates from Zoho ({data?.total_from_zoho ?? 0})
          </span>

          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="brand">{data?.new.length ?? 0} new</Badge>
            <Badge variant="warning">{data?.changed.length ?? 0} changed</Badge>
            <Badge variant="neutral">{data?.same.length ?? 0} unchanged</Badge>
          </div>

          {nothingToDo && (
            <span className="text-body font-body text-subtext-color">
              Everything here already matches Zoho. Nothing to save.
            </span>
          )}

          {data !== undefined && data.changed.length > 0 && (
            <div className="max-h-80 w-full overflow-auto rounded-md border border-solid border-neutral-border">
              {data.changed.map((row) => (
                <div
                  key={row.tcs_id}
                  className="flex w-full flex-col gap-0.5 border-b border-solid border-neutral-border px-4 py-2.5 last:border-b-0"
                >
                  <span className="text-body font-body text-default-font">
                    {row.tax_name}
                  </span>
                  {Object.entries(row.changes).map(([field, change]) => (
                    <span
                      key={field}
                      className="text-caption font-caption text-amber-700"
                    >
                      {FIELD_LABELS[field] ?? field}: {change.old || "(blank)"} →{" "}
                      {change.new || "(blank)"}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          )}

          {applied && (
            <div className="flex w-full items-start rounded-lg border border-solid border-green-200 bg-green-50 p-3">
              <span className="text-body font-body text-green-800">{applied}</span>
            </div>
          )}
          {error && (
            <div className="flex w-full items-start rounded-lg border border-solid border-red-200 bg-red-50 p-3">
              <span className="text-body font-body text-red-600">{error}</span>
            </div>
          )}

          <div className="flex w-full items-center justify-end gap-2">
            <Button
              variant="neutral-tertiary"
              onClick={() => {
                setOpen(false);
                setApplied(null);
              }}
              disabled={busy}
            >
              {applied ? "Close" : "Cancel"}
            </Button>
            {!applied && (
              <Button
                variant="brand-primary"
                onClick={handleApply}
                disabled={busy || nothingToDo}
              >
                {busy ? "Saving..." : "Save to zournal"}
              </Button>
            )}
          </div>
        </div>
      </DialogLayout>
    </>
  );
};
